import type { ExpValue, ExpExactCandidate, GameStateSnapshot, PartialExpStatistics } from '@/domain/types'
import { expRequirementRange, intersectExpRequirementRanges, type ExpRequirementRange } from './exp-requirement-estimator'
import { SessionStartExpConsensus, type ConfirmedSessionStartExp } from './session-start-baseline'

/** Conservative, local-only counting. No level, upgrade or death is inferred. */
export class UnknownLevelExpTracker {
  private anchor: ExpExactCandidate | null = null
  private pending: ExpExactCandidate | null = null
  private range: ExpRequirementRange | null = null
  private segments: { startedAt: number; endedAt: number; gained: number }[] = []
  private gaps: { startedAt: number; endedAt: number | null; reason: PartialExpStatistics['gaps'][number]['reason'] }[] = []
  private recovery: SessionStartExpConsensus | null = null
  private latestAt = -Infinity
  private boundary = -Infinity
  private recovered = false
  private lastConfirmedExp: ExpValue | undefined
  private gapMs = 15_000

  constructor(state: GameStateSnapshot, minimumAt: number) {
    this.boundary = minimumAt
    if (state.exp.absolute === null || state.exp.ratio === null) return
    this.anchor = { value: { ...state.exp, absolute: state.exp.absolute, ratio: state.exp.ratio },
      level: null, levelSourceAt: null, sourceAt: state.observedAt?.exp ?? state.capturedAt,
      deliveredAt: state.capturedAt, confidence: state.confidence.exp,
      percentagePrecision: state.exp.percentagePrecision ?? 2, barConsistency: 'unavailable', recognitionPath: 'legacy' }
    this.lastConfirmedExp = this.anchor.value
    this.latestAt = this.anchor.sourceAt
    this.range = expRequirementRange(this.anchor.value, this.anchor.percentagePrecision) ?? null
  }

  setSamplingInterval(intervalMs: number): void { this.gapMs = Math.max(15_000, intervalMs * 3) }

  breakContinuity(at: number, reason: PartialExpStatistics['gaps'][number]['reason'] = 'interrupted'): void {
    if (this.recovered) return
    const start = this.anchor?.sourceAt ?? this.pending?.sourceAt ?? at
    if (!this.gaps.length || this.gaps.at(-1)!.endedAt !== null) this.gaps.push({ startedAt: start, endedAt: null, reason })
    this.anchor = this.pending = null
    this.recovery = null
    this.range = null
    this.boundary = Math.max(this.boundary, at)
  }

  observe(sample: ExpExactCandidate | undefined, now: number, minimumConfidence: number): ConfirmedSessionStartExp | null {
    if (this.recovered) return null
    if (!sample || now - sample.sourceAt > this.gapMs) {
      if (now - this.latestAt > this.gapMs) this.breakContinuity(now, 'unreadable')
      return null
    }
    if (sample.sourceAt <= this.latestAt || sample.sourceAt < this.boundary) return null
    this.latestAt = sample.sourceAt
    const range = expRequirementRange(sample.value, sample.percentagePrecision)
    if (!Number.isFinite(sample.confidence) || sample.confidence < minimumConfidence || range === undefined
      || sample.value.estimated || sample.barConsistency === 'conflict' || sample.deliveredAt < sample.sourceAt || sample.sourceAt > now) {
      this.breakContinuity(sample.sourceAt, 'conflict')
      return null
    }
    const previous = this.anchor ?? this.pending
    let compatible = !!previous
    let merged: ExpRequirementRange | null | undefined = range
    if (previous) {
      const priorRange = this.range ?? expRequirementRange(previous.value, previous.percentagePrecision)
      const delta = sample.value.absolute - previous.value.absolute
      merged = range && priorRange ? intersectExpRequirementRanges([range, priorRange]) : range ?? priorRange
      const sourceChanged = previous.value.sourceId !== sample.value.sourceId
      const elapsed = sample.sourceAt - previous.sourceAt
      // With no level, a large jump is a gap, never an invented upgrade.
      const bound = Math.max(1_000, (merged?.minimum ?? previous.value.absolute) * .05)
      compatible = !sourceChanged && elapsed <= this.gapMs && delta >= 0 && delta <= bound
        && sample.value.ratio >= previous.value.ratio && merged !== undefined
      if (!compatible) this.breakContinuity(sample.sourceAt, sourceChanged ? 'source-changed'
        : elapsed > this.gapMs ? 'unreadable' : delta < 0 ? 'decrease' : 'conflict')
    }
    if (compatible && previous) {
      if (this.anchor) {
        const delta = sample.value.absolute - previous.value.absolute
        const last = this.segments.at(-1)
        if (last?.endedAt === previous.sourceAt) { last.endedAt = sample.sourceAt; last.gained += delta }
        else this.segments.push({ startedAt: previous.sourceAt, endedAt: sample.sourceAt, gained: delta })
      }
      // After a gap, two frames establish a new origin. Never backfill the gap.
      const gap = this.gaps.at(-1)
      if (gap && gap.endedAt === null) gap.endedAt = sample.sourceAt
      this.lastConfirmedExp = sample.value
      this.anchor = sample
      this.pending = null
      this.range = merged ?? null
    } else {
      this.pending = sample
      this.range = range
    }
    if (sample.level === null || sample.levelSourceAt === null || sample.levelSourceAt > sample.sourceAt
      || sample.sourceAt - sample.levelSourceAt > this.gapMs) { this.recovery = null; return null }
    this.recovery ??= new SessionStartExpConsensus(sample.sourceAt, minimumConfidence)
    const result = this.recovery.observe(sample)
    if (result.confirmed) {
      this.recovered = true
      return result.confirmed
    }
    return null
  }

  snapshot(): PartialExpStatistics {
    return { lastConfirmedExp: this.lastConfirmedExp, basis: 'confirmed-segments', waitingForLevel: !this.recovered,
      gained: this.segments.reduce((sum, segment) => sum + segment.gained, 0),
      durationMs: this.segments.reduce((sum, segment) => sum + segment.endedAt - segment.startedAt, 0),
      segments: this.segments.map(segment => ({ ...segment })), gaps: this.gaps.map(gap => ({ ...gap })) }
  }
}
